export const METER_HOUR = "hour_meter";
export const METER_ODOMETER = "odometer";
export const METER_BOTH = "both";

export function normalizeMeterType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (
    raw === METER_ODOMETER ||
    raw === "km" ||
    raw === "odometro" ||
    raw === "quilometros"
  ) {
    return METER_ODOMETER;
  }
  if (raw === METER_BOTH || raw === "ambos") {
    return METER_BOTH;
  }
  return METER_HOUR;
}

export function resolveMeterPresentation(meterType, t) {
  const normalized = normalizeMeterType(meterType);

  if (normalized === METER_ODOMETER) {
    return {
      meterType: normalized,
      usesHours: false,
      usesKm: true,
      panelWorkedLabel: t("Quilômetros no painel da máquina"),
      panelRealLabel: t("Quilômetros reais no painel"),
      panelWorkedHint: t(
        "Diferença entre a leitura final e inicial do hodômetro informada ao assumir e finalizar a máquina."
      ),
      panelColumnHint: t(
        "Soma dos quilômetros e hectares de todos os turnos no período."
      ),
      showIdleEngine: false,
      snapshotHourLabel: null,
      snapshotOdometerLabel: t("Hodômetro"),
    };
  }

  if (normalized === METER_BOTH) {
    return {
      meterType: normalized,
      usesHours: true,
      usesKm: true,
      panelWorkedLabel: t("Horas no painel da máquina"),
      panelWorkedKmLabel: t("Quilômetros no painel da máquina"),
      panelRealLabel: t("Horas reais no painel"),
      panelRealKmLabel: t("Quilômetros reais no painel"),
      panelWorkedHint: t(
        "Diferença entre a leitura final e inicial do horímetro informada ao assumir e finalizar a máquina."
      ),
      panelWorkedKmHint: t(
        "Diferença entre a leitura final e inicial do hodômetro informada ao assumir e finalizar a máquina."
      ),
      panelColumnHint: t(
        "Soma das horas, quilômetros e hectares de todos os turnos no período."
      ),
      showIdleEngine: true,
      snapshotHourLabel: t("Horímetro"),
      snapshotOdometerLabel: t("Hodômetro"),
    };
  }

  return {
    meterType: normalized,
    usesHours: true,
    usesKm: false,
    panelWorkedLabel: t("Horas no painel da máquina"),
    panelRealLabel: t("Horas reais no painel"),
    panelWorkedHint: t(
      "Diferença entre a leitura final e inicial do horímetro informada ao assumir e finalizar a máquina."
    ),
    panelColumnHint: t(
      "Soma das horas e hectares de todos os turnos no período."
    ),
    showIdleEngine: true,
    snapshotHourLabel: t("Horímetro"),
    snapshotOdometerLabel: null,
  };
}

export function formatKm(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} km`;
}

export function resolvePanelReadingValue({ hourMeter, odometer }, meterType) {
  const normalized = normalizeMeterType(meterType);

  if (normalized === METER_ODOMETER) {
    if (odometer != null && odometer !== "") return odometer;
    if (hourMeter != null && hourMeter !== "") return hourMeter;
    return null;
  }

  if (normalized === METER_HOUR) {
    if (hourMeter != null && hourMeter !== "") return hourMeter;
    if (odometer != null && odometer !== "") return odometer;
    return null;
  }

  return null;
}

export function formatPanelReadingDisplay(value, meterType) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  if (normalizeMeterType(meterType) === METER_ODOMETER) {
    return `${num.toLocaleString("pt-BR")} km`;
  }
  return `${num.toLocaleString("pt-BR")} h`;
}

export function resolveDisplayedPanelKm({
  workedKm,
  pathDistanceKm,
  workedKmFromGps,
  meterPresentation,
  t,
}) {
  if (!meterPresentation || !meterPresentation.usesKm) {
    return null;
  }

  const gpsHint = t(
    "Distância calculada pelo percurso GPS no período, pois não há leituras de hodômetro informadas pelo operador."
  );
  const gpsLabel = t("Quilômetros percorridos (GPS)");
  const panelPathKm =
    pathDistanceKm != null && Number(pathDistanceKm) > 0
      ? Number(pathDistanceKm)
      : null;

  if (workedKm != null && Number(workedKm) > 0) {
    const fromGps = Boolean(workedKmFromGps);
    return {
      value: Number(workedKm),
      label: fromGps
        ? gpsLabel
        : meterPresentation.meterType === METER_BOTH
          ? meterPresentation.panelWorkedKmLabel ||
            meterPresentation.panelWorkedLabel
          : meterPresentation.panelWorkedLabel,
      hint: fromGps
        ? gpsHint
        : meterPresentation.panelWorkedKmHint ||
          meterPresentation.panelWorkedHint,
    };
  }

  if (panelPathKm != null) {
    return {
      value: panelPathKm,
      label: gpsLabel,
      hint: gpsHint,
    };
  }

  return null;
}

export function resolveDisplayedTelemetryKm({
  odometerKm,
  odometerKmFromGps,
  pathDistanceKm,
  meterPresentation,
  t,
}) {
  if (!meterPresentation || !meterPresentation.usesKm) {
    return null;
  }

  const gpsHint = t(
    "Distância calculada pelo percurso GPS no turno, pois não há leituras de hodômetro informadas pelo operador."
  );
  const gpsLabel = t("Quilômetros percorridos (GPS)");
  const panelPathKm =
    pathDistanceKm != null && Number(pathDistanceKm) > 0
      ? Number(pathDistanceKm)
      : null;

  if (odometerKm != null && Number(odometerKm) > 0) {
    const fromGps = Boolean(odometerKmFromGps);
    return {
      value: Number(odometerKm),
      label: fromGps
        ? gpsLabel
        : meterPresentation.meterType === METER_BOTH
          ? meterPresentation.panelRealKmLabel ||
            t("Quilômetros reais no painel")
          : meterPresentation.panelRealLabel,
      hint: fromGps
        ? gpsHint
        : meterPresentation.panelWorkedKmHint ||
          meterPresentation.panelWorkedHint,
    };
  }

  if (panelPathKm != null) {
    return {
      value: panelPathKm,
      label: gpsLabel,
      hint: gpsHint,
    };
  }

  return null;
}
